import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ShieldAlert, Activity, KeyRound, RotateCcw, Plus, Trash2, Copy, CheckCircle2, Banknote, Mail, MessageSquare, Send } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

function Pill({ children, tone = "muted" }) {
  const map = {
    high: "bg-[#c15337]/12 text-[#8b3a23] border-[#c15337]/25",
    medium: "bg-[#d49a36]/15 text-[#8a5a16] border-[#d49a36]/30",
    low: "bg-[#114232]/8 text-[#114232] border-[#114232]/15",
    muted: "bg-stone-100 text-stone-700 border-stone-200",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${map[tone] || map.muted}`}>
      {children}
    </span>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2.5">
        <Icon className="w-5 h-5 text-[#c15337]" />
        <div>
          <h2 className="font-heading text-lg font-bold text-[#114232]">{title}</h2>
          {subtitle && <div className="text-xs text-[#5c6360]">{subtitle}</div>}
        </div>
      </div>
      {action}
    </div>
  );
}

function Anomalies() {
  const [alerts, setAlerts] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [resolveNote, setResolveNote] = useState("");

  async function load() {
    try {
      const { data } = await api.get("/admin/anomalies?limit=50");
      setAlerts(data.items || []);
      setOpenCount(data.open_count || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => { load(); }, []);

  async function scan() {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/anomalies/scan");
      const total = Object.values(data.results || {})
        .reduce((s, v) => s + (typeof v === "number" ? v : 0), 0);
      toast.success(`Scan complete — ${total} new alert${total === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setScanning(false);
    }
  }

  async function submitResolve() {
    if (!resolving || resolveNote.trim().length < 2) {
      toast.error("Resolution note must be ≥ 2 characters.");
      return;
    }
    try {
      await api.post(`/admin/anomalies/${resolving}/resolve`,
        { resolution_note: resolveNote.trim() });
      toast.success("Alert resolved");
      setResolving(null);
      setResolveNote("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="bg-white border border-[#114232]/10 rounded-md p-6" data-testid="anomalies-card">
      <SectionHeader
        icon={Activity}
        title="Anomaly inbox"
        subtitle={`${openCount} open · scans every 30 min · high-severity alerts auto-push to Slack/email`}
        action={
          <button
            type="button"
            onClick={scan}
            disabled={scanning}
            data-testid="run-anomaly-scan-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-bold border border-[#114232]/15 rounded text-[#114232] hover:bg-[#114232]/5 disabled:opacity-50"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${scanning ? "animate-spin" : ""}`} />
            {scanning ? "Scanning…" : "Scan now"}
          </button>
        }
      />
      <div className="space-y-2" data-testid="anomalies-list">
        {alerts.length === 0 ? (
          <div className="text-sm text-[#5c6360] py-6 text-center">No anomalies detected.</div>
        ) : alerts.map((a) => (
          <div key={a.id} data-testid={`anomaly-row-${a.id}`}
                className={`border rounded p-3 ${a.resolved_at ? "border-stone-200 bg-stone-50/50 opacity-70" : "border-[#c15337]/20 bg-[#c15337]/5"}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <Pill tone={a.severity}>{a.severity}</Pill>
                <span className="text-xs font-mono text-[#5c6360]">{a.kind?.replace(/_/g, " ")}</span>
              </div>
              <span className="text-[10px] font-mono text-[#5c6360]">{a.created_at?.slice(0, 16).replace("T", " ")}</span>
            </div>
            <div className="text-sm text-[#114232]">{a.message}</div>
            {a.merchant_id && (
              <div className="text-[10px] font-mono text-[#5c6360] mt-1">merchant: {a.merchant_id.slice(0, 8)}…</div>
            )}
            {a.resolved_at ? (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-[#114232]">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Resolved · {a.resolution_note}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setResolving(a.id); setResolveNote(""); }}
                data-testid={`anomaly-resolve-${a.id}`}
                className="mt-2 text-[11px] uppercase tracking-wider font-bold text-[#c15337] hover:underline"
              >
                Mark resolved
              </button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!resolving} onOpenChange={(v) => { if (!v) { setResolving(null); setResolveNote(""); } }}>
        <DialogContent data-testid="resolve-alert-dialog">
          <DialogHeader>
            <DialogTitle>Resolve this anomaly</DialogTitle>
            <DialogDescription>Add a short note explaining what action you took. Visible in the audit trail.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="resolve-note">Resolution note</Label>
            <Textarea
              id="resolve-note"
              data-testid="resolve-note-input"
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              placeholder="e.g. Verified with merchant — claims were legitimate after payroll glitch."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolving(null)} data-testid="resolve-cancel-btn">Cancel</Button>
            <Button onClick={submitResolve} data-testid="resolve-confirm-btn" className="bg-[#c15337] hover:bg-[#a8462e]">Resolve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Reversals() {
  const [rev, setRev] = useState([]);
  const [claw, setClaw] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/reversals?limit=20"),
      api.get("/admin/clawbacks?limit=20"),
    ])
      .then(([r1, r2]) => {
        setRev(r1.data.items || []);
        setClaw(r2.data.items || []);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  return (
    <div className="bg-white border border-[#114232]/10 rounded-md p-6" data-testid="reversals-card">
      <SectionHeader
        icon={ShieldAlert}
        title="Reversal protection"
        subtitle={`${rev.length} reversed premiums · ${claw.length} clawed-back claims`}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#5c6360] mb-2">Reversed premiums</h3>
          <ul className="space-y-1.5" data-testid="reversed-premiums-list">
            {rev.length === 0 ? (
              <li className="text-xs text-[#5c6360] py-3 text-center border border-stone-200 rounded">None — your capital is intact.</li>
            ) : rev.map((p) => (
              <li key={p.id} className="text-xs p-2 border border-[#c15337]/15 rounded" data-testid={`reversed-${p.id}`}>
                <div className="font-mono">KES {p.amount?.toLocaleString()} · {p.provider}</div>
                <div className="text-[10px] text-[#5c6360]">reason: {p.reversal_reason}</div>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#5c6360] mb-2">Clawed-back claims</h3>
          <ul className="space-y-1.5" data-testid="clawbacks-list">
            {claw.length === 0 ? (
              <li className="text-xs text-[#5c6360] py-3 text-center border border-stone-200 rounded">None recorded.</li>
            ) : claw.map((c) => (
              <li key={c.id} className="text-xs p-2 border border-[#c15337]/15 rounded" data-testid={`clawback-${c.id}`}>
                <div className="font-mono">KES {c.clawback_recovered_kes?.toLocaleString()} · #{c.id?.slice(0, 8)}</div>
                <div className="text-[10px] text-[#5c6360]">{c.clawback_reason}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [newSecret, setNewSecret] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [revoking, setRevoking] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/admin/api-keys");
      setKeys(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => { load(); }, []);

  async function create() {
    const name = newKeyName.trim();
    if (name.length < 2) {
      toast.error("Name must be ≥ 2 characters.");
      return;
    }
    try {
      const { data } = await api.post("/admin/api-keys", {
        name, scopes: ["signup:create"],
      });
      setNewSecret(data);
      setShowCreate(false);
      setNewKeyName("");
      await load();
      toast.success("API key issued — copy the secret now (shown once).");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function confirmRevoke() {
    if (!revoking) return;
    try {
      await api.delete(`/admin/api-keys/${revoking}`);
      toast.success("Key revoked");
      setRevoking(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="bg-white border border-[#114232]/10 rounded-md p-6" data-testid="api-keys-card">
      <SectionHeader
        icon={KeyRound}
        title="Public API keys"
        subtitle="Used by partners + the embeddable signup widget"
        action={
          <button
            type="button"
            onClick={() => { setShowCreate(true); setNewKeyName(""); }}
            data-testid="create-api-key-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-bold bg-[#c15337] text-white rounded hover:opacity-90"
          >
            <Plus className="w-3.5 h-3.5" /> New key
          </button>
        }
      />
      {newSecret && (
        <div className="mb-4 p-3 border border-[#d49a36]/40 bg-[#d49a36]/10 rounded" data-testid="new-key-banner">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#8a5a16] mb-1">Copy this secret now</div>
          <div className="flex items-center gap-2">
            <code className="text-xs font-mono bg-white px-2 py-1 rounded flex-1 break-all" data-testid="new-key-secret">{newSecret.secret}</code>
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(newSecret.secret); toast.success("Copied"); }}
              className="p-1.5 rounded hover:bg-white/60"
              data-testid="copy-new-key-btn"
            >
              <Copy className="w-3.5 h-3.5 text-[#8a5a16]" />
            </button>
            <button
              type="button"
              onClick={() => setNewSecret(null)}
              className="text-[11px] font-bold text-[#8a5a16] uppercase tracking-wider"
            >
              Done
            </button>
          </div>
        </div>
      )}
      <ul className="space-y-2" data-testid="api-keys-list">
        {keys.length === 0 ? (
          <li className="text-sm text-[#5c6360] py-6 text-center">No keys issued yet.</li>
        ) : keys.map((k) => (
          <li key={k.id} data-testid={`api-key-row-${k.id}`}
                className={`flex items-center justify-between gap-3 p-3 border rounded ${k.revoked_at ? "border-stone-200 bg-stone-50 opacity-60" : "border-[#114232]/15"}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-[#114232]">{k.name}</div>
              <div className="text-[10px] font-mono text-[#5c6360] flex items-center gap-2 flex-wrap">
                <span>{k.prefix}…</span>
                <span>scopes: {(k.scopes || []).join(", ")}</span>
                {k.partner_id && <span>partner: {k.partner_id.slice(0, 8)}</span>}
                {k.last_used_at && <span>last used: {k.last_used_at.slice(0, 16).replace("T", " ")}</span>}
              </div>
            </div>
            {k.revoked_at ? (
              <Pill tone="low">revoked</Pill>
            ) : (
              <button
                type="button"
                onClick={() => setRevoking(k.id)}
                data-testid={`revoke-key-${k.id}`}
                className="p-1.5 rounded hover:bg-[#c15337]/10 text-[#c15337]"
                title="Revoke"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </li>
        ))}
      </ul>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-api-key-dialog">
          <DialogHeader>
            <DialogTitle>Issue a new API key</DialogTitle>
            <DialogDescription>
              The full secret is displayed only once — store it in your password manager before closing the next dialog.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="key-name">Key name</Label>
            <Input
              id="key-name"
              data-testid="new-key-name-input"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="e.g. KCB iOS app · production"
              autoFocus
            />
            <div className="text-xs text-[#5c6360]">Scopes: <code className="font-mono">signup:create</code></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} data-testid="create-key-cancel-btn">Cancel</Button>
            <Button onClick={create} data-testid="create-key-confirm-btn" className="bg-[#c15337] hover:bg-[#a8462e]">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Issue key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!revoking} onOpenChange={(v) => { if (!v) setRevoking(null); }}>
        <DialogContent data-testid="revoke-key-dialog">
          <DialogHeader>
            <DialogTitle>Revoke this API key?</DialogTitle>
            <DialogDescription>
              Any app using this key will immediately fail with 401. This cannot be undone — issue a new key if you change your mind.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevoking(null)} data-testid="revoke-cancel-btn">Cancel</Button>
            <Button onClick={confirmRevoke} data-testid="revoke-confirm-btn" className="bg-[#c15337] hover:bg-[#a8462e]">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Security() {
  return (
    <div className="px-8 py-6 space-y-6 max-w-6xl" data-testid="security-page">
      <header className="mb-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#c15337] font-bold mb-1">Protection layer</div>
        <h1 className="font-heading text-3xl font-bold text-[#114232]">Security & trust</h1>
        <p className="text-sm text-[#5c6360] mt-1 max-w-2xl">
          Anomaly inbox, reversal protection, banking-rail wiring, the outbound queue, and the public API keys that power the embeddable signup widget.
        </p>
      </header>
      <Anomalies />
      <Reversals />
      <BankingRailStatus />
      <SlackHealthCard />
      <OutboundQueue />
      <ApiKeys />
    </div>
  );
}

function SlackHealthCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  function load() {
    setBusy(true);
    api.get("/integrations/slack/health")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setBusy(false));
  }
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const live = data.live;
  const strict = data.strict_signing;
  const reason = data.auth_test?.reason || "";
  const channelOK = !!data.alerts_channel_id;
  return (
    <div className="bg-white border border-[#114232]/10 rounded-md p-6" data-testid="slack-health-card">
      <SectionHeader
        icon={Activity}
        title="Slack integration"
        subtitle="Bot token, signing secret, and alerts channel wiring"
      />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="slack-health-grid">
        <div data-testid="slack-health-bot-token"
              className={`border rounded-md p-3 ${live ? "border-[#114232]/25 bg-[#114232]/4" : "border-stone-200 bg-stone-50/40"}`}>
          <div className="text-[10px] uppercase tracking-wider text-[#5c6360]">Bot token</div>
          <div className="font-heading text-sm font-bold mt-1">
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border mr-2 ${live ? "bg-[#114232]/12 text-[#114232] border-[#114232]/30" : "bg-stone-100 text-stone-600 border-stone-300"}`}>
              {live ? "LIVE" : "MOCK"}
            </span>
            {live ? data.auth_test?.team || "connected" : "Configure SLACK_BOT_TOKEN"}
          </div>
        </div>
        <div data-testid="slack-health-signing"
              className={`border rounded-md p-3 ${strict ? "border-[#114232]/25 bg-[#114232]/4" : "border-stone-200 bg-stone-50/40"}`}>
          <div className="text-[10px] uppercase tracking-wider text-[#5c6360]">Signing secret</div>
          <div className="font-heading text-sm font-bold mt-1">
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border mr-2 ${strict ? "bg-[#114232]/12 text-[#114232] border-[#114232]/30" : "bg-stone-100 text-stone-600 border-stone-300"}`}>
              {strict ? "STRICT" : "PERMISSIVE"}
            </span>
            {strict ? "HMAC enforced" : "Bypass enabled"}
          </div>
        </div>
        <div data-testid="slack-health-channel"
              className={`border rounded-md p-3 ${channelOK ? "border-[#114232]/25 bg-[#114232]/4" : "border-stone-200 bg-stone-50/40"}`}>
          <div className="text-[10px] uppercase tracking-wider text-[#5c6360]">Alerts channel</div>
          <div className="font-heading text-sm font-bold mt-1 font-mono text-xs">
            {channelOK ? data.alerts_channel_id : "Configure SLACK_ALERTS_CHANNEL_ID"}
          </div>
        </div>
      </div>
      {!live && reason && (
        <div className="mt-3 text-xs text-[#5c6360]" data-testid="slack-health-reason">
          {reason}
        </div>
      )}
      <div className="mt-3">
        <button
          onClick={load}
          disabled={busy}
          data-testid="slack-health-refresh"
          className="text-xs underline text-[#5c6360] hover:text-[#114232]"
        >
          {busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>
    </div>
  );
}

function BankingRailStatus() {
  const [rails, setRails] = useState(null);

  useEffect(() => {
    api.get("/admin/banking/status")
      .then((r) => setRails(r.data.rails || {}))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  if (!rails) return null;

  return (
    <div className="bg-white border border-[#114232]/10 rounded-md p-6" data-testid="banking-rails-card">
      <SectionHeader
        icon={Banknote}
        title="Banking rails"
        subtitle="Direct partnership integrations (KCB Buni, Equity Jenga)"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="banking-rails-grid">
        {Object.entries(rails).map(([prov, info]) => (
          <div key={prov} data-testid={`banking-rail-${prov}`}
                className={`border rounded-md p-4 ${info.live ? "border-[#114232]/25 bg-[#114232]/4" : "border-stone-200 bg-stone-50/40"}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="font-heading text-base font-bold text-[#114232] uppercase">{prov}</div>
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${info.live ? "bg-[#114232]/12 text-[#114232] border-[#114232]/30" : "bg-stone-100 text-stone-600 border-stone-300"}`}>
                {info.live ? "LIVE" : "MOCK"}
              </span>
            </div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#5c6360] mt-3 mb-1">API base</div>
            <code className="block text-[11px] font-mono text-[#114232] break-all">{info.api_base || "—"}</code>
            <div className="flex items-center gap-1.5 mt-2 text-[11px]">
              {info.has_webhook_secret ? (
                <><CheckCircle2 className="w-3.5 h-3.5 text-[#114232]" /><span>Webhook secret configured</span></>
              ) : (
                <span className="text-[#c15337]">⚠ Webhook secret missing — set <code className="font-mono">WEBHOOK_SECRET_{prov.toUpperCase()}</code></span>
              )}
            </div>
            {!info.live && (
              <div className="mt-3 text-[10px] text-[#5c6360]">
                To flip live: set <code className="font-mono">LIVE_{prov.toUpperCase()}=true</code> + valid <code className="font-mono">{prov.toUpperCase()}_API_KEY</code> in <code className="font-mono">backend/.env</code>.
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function OutboundQueue() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ email: 0, sms: 0, all: 0 });
  const [tab, setTab] = useState("all");
  const [sending, setSending] = useState(null);

  async function load() {
    try {
      const { data } = await api.get(`/admin/outbound?kind=${tab}&limit=20`);
      setItems(data.items || []);
      setTotals(data.totals || { email: 0, sms: 0, all: 0 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => { load(); }, [tab]);

  async function markSent(row) {
    setSending(row.id);
    try {
      await api.post(`/admin/outbound/${row.type}/${row.id}/mark-sent`);
      toast.success("Marked as sent");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSending(null);
    }
  }

  return (
    <div className="bg-white border border-[#114232]/10 rounded-md p-6" data-testid="outbound-queue-card">
      <SectionHeader
        icon={Mail}
        title="Outbound queue"
        subtitle={`${totals.all} rows total · ${totals.email} email · ${totals.sms} SMS`}
        action={
          <div className="flex items-center gap-1 text-[11px]" data-testid="outbound-tabs">
            {[["all", "All"], ["email", "Email"], ["sms", "SMS"]].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={`outbound-tab-${k}`}
                className={`px-2.5 py-1 rounded border uppercase tracking-wider font-bold transition-colors ${tab === k ? "bg-[#114232] text-white border-[#114232]" : "bg-white text-[#5c6360] border-[#114232]/15 hover:border-[#114232]/40"}`}
              >
                {label}
              </button>
            ))}
          </div>
        }
      />
      <ul className="space-y-1.5" data-testid="outbound-queue-list">
        {items.length === 0 ? (
          <li className="text-sm text-[#5c6360] py-6 text-center">No outbound rows yet.</li>
        ) : items.map((r) => (
          <li key={`${r.type}-${r.id}`} data-testid={`outbound-row-${r.id}`}
              className={`flex items-center justify-between gap-3 p-2.5 border rounded ${r.sent_at ? "border-stone-200 bg-stone-50 opacity-70" : "border-[#114232]/15"}`}>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {r.type === "email" ? (
                <Mail className="w-3.5 h-3.5 text-[#114232] flex-none" />
              ) : (
                <MessageSquare className="w-3.5 h-3.5 text-[#c15337] flex-none" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#114232] truncate">
                  {r.subject || r.body_preview || "—"}
                </div>
                <div className="text-[10px] font-mono text-[#5c6360] flex items-center gap-1.5 flex-wrap">
                  <span>→ {r.to}</span>
                  <span>·</span>
                  <span>{r.provider}</span>
                  <span>·</span>
                  <span>{r.created_at?.slice(0, 16).replace("T", " ")}</span>
                  {r.attachments?.length > 0 && <span className="text-[#c15337]">📎 {r.attachments[0].filename}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Pill tone={r.sent_at ? "low" : "medium"}>{r.sent_at ? "sent" : "queued"}</Pill>
              {!r.sent_at && (
                <button
                  type="button"
                  onClick={() => markSent(r)}
                  disabled={sending === r.id}
                  data-testid={`outbound-mark-sent-${r.id}`}
                  className="p-1.5 rounded hover:bg-[#114232]/10 text-[#114232] disabled:opacity-50"
                  title="Mark as sent"
                >
                  <Send className={`w-3.5 h-3.5 ${sending === r.id ? "animate-pulse" : ""}`} />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
